import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { serviceLogoUrl, services, type Service, type ServiceLogo } from '../data/services';

function PreviewList({
  title,
  icon,
  items,
}: {
  title: string;
  icon: string;
  items: string[];
}) {
  return (
    <div className="service-preview-block">
      <h3>{title}</h3>
      <ul className="service-detail-list">
        {items.slice(0, 2).map((item) => (
          <li key={item}>
            <span className="material-symbols-outlined">{icon}</span>
            <span>{item}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function LogoBadge({ logo }: { logo: ServiceLogo }) {
  return (
    <div className="service-logo-badge" title={logo.name}>
      <img src={serviceLogoUrl(logo.slug)} alt={logo.name} loading="lazy" />
      <span>{logo.name}</span>
    </div>
  );
}

function ServiceLogos({ logos, expanded }: { logos: ServiceLogo[]; expanded: boolean }) {
  const visible = expanded ? logos : logos.slice(0, 3);
  const hiddenCount = Math.max(0, logos.length - visible.length);

  return (
    <div className={`service-logo-panel${expanded ? ' is-expanded' : ''}`}>
      <div className="service-logo-panel-head">
        <h3>{expanded ? 'Examples of Systems we Integrate Seamlessly' : 'Integrations'}</h3>
        {!expanded && hiddenCount > 0 && <span className="muted">+{hiddenCount} more</span>}
      </div>
      <div className="service-logo-grid">
        {visible.map((logo) => (
          <LogoBadge key={`${logo.slug}-${logo.name}`} logo={logo} />
        ))}
      </div>
      <p className="service-logo-note">
        Don&apos;t see your software? Don&apos;t worry, we can integrate with thousands of platforms!
      </p>
    </div>
  );
}

function ServiceCard({
  service,
  expanded,
  onToggle,
}: {
  service: Service;
  expanded: boolean;
  onToggle: () => void;
}) {
  return (
    <article
      id={service.slug}
      className={`card service-detail${expanded ? ' is-expanded' : ''}`}
      aria-expanded={expanded}
    >
      <div className="service-detail-copy">
        <div className="service-detail-kicker">
          <div className="service-icon">
            <span className="material-symbols-outlined">{service.icon}</span>
          </div>
        </div>

        <h2>{service.name}</h2>
        <p className="service-detail-tagline">{service.tagline}</p>
        <p className={`service-detail-description${expanded ? ' is-full' : ''}`}>{service.description}</p>

        <div className={`service-detail-extra${expanded ? ' is-open' : ''}`}>
          <div className="service-detail-columns">
            <div>
              <h3>What you get</h3>
              <ul className="service-detail-list">
                {service.capabilities.map((item) => (
                  <li key={item}>
                    <span className="material-symbols-outlined">check_circle</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h3>Outcomes</h3>
              <ul className="service-detail-list">
                {service.outcomes.map((item) => (
                  <li key={item}>
                    <span className="material-symbols-outlined">trending_up</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        <button
          type="button"
          className="btn btn-ghost service-expand-btn"
          onClick={onToggle}
          aria-expanded={expanded}
        >
          {expanded ? 'Show less' : 'View full details'}
        </button>
      </div>

      <div className="service-detail-media">
        <button
          type="button"
          className="service-logo-hit"
          onClick={onToggle}
          aria-label={expanded ? `Collapse ${service.name}` : `Expand ${service.name} and view all systems`}
        >
          <ServiceLogos logos={service.logos} expanded={expanded} />
        </button>

        {!expanded && (
          <div className="service-media-preview">
            <PreviewList title="What you get" icon="check_circle" items={service.capabilities} />
            <PreviewList title="Outcomes" icon="trending_up" items={service.outcomes} />
          </div>
        )}
      </div>
    </article>
  );
}

function pairServices(list: Service[]) {
  const pairs: Service[][] = [];
  for (let i = 0; i < list.length; i += 2) {
    pairs.push(list.slice(i, i + 2));
  }
  return pairs;
}

export function ServicesPage() {
  const { hash } = useLocation();
  const activeSlug = hash ? decodeURIComponent(hash.replace(/^#/, '')) : '';
  const [expandedSlug, setExpandedSlug] = useState<string | null>(activeSlug || null);

  useEffect(() => {
    if (activeSlug) setExpandedSlug(activeSlug);
  }, [activeSlug]);

  const pairs = useMemo(() => pairServices(services), []);

  function toggle(slug: string) {
    setExpandedSlug((current) => (current === slug ? null : slug));
  }

  return (
    <div className="container services-page">
      <div className="page-hero services-page-hero">
        <span className="eyebrow">Services</span>
        <h1>
          Custom AI & automation built around <span className="gradient-text">how you actually work</span>
        </h1>
        <p>
          Ask yourself: What is my biggest headache? See if any of these answer that problem. Browse the full service
          lineup below — larger write-ups, clear outcomes, and visual previews for every offering. These are some
          offerings of our capabilities, however everything we do is custom and tailored to your needs. Book a
          consultation to find out more!
        </p>
        <div className="services-page-actions">
          <a className="btn btn-primary" href="#contact-cta">
            Get Started
          </a>
          <Link className="btn btn-ghost" to="/marketplace">
            Browse Marketplace
          </Link>
        </div>
      </div>

      <nav className="services-jump" aria-label="Jump to a service">
        {services.map((service) => (
          <a key={service.id} className="chip" href={`#${service.slug}`}>
            {service.name}
          </a>
        ))}
      </nav>

      <div className="services-detail-list">
        {pairs.map((pair) => {
          const rowExpanded = pair.some((service) => service.slug === expandedSlug);
          const ordered = rowExpanded
            ? [
                ...pair.filter((service) => service.slug === expandedSlug),
                ...pair.filter((service) => service.slug !== expandedSlug),
              ]
            : pair;

          return (
            <div
              key={pair.map((service) => service.id).join('-')}
              className={`services-detail-row${rowExpanded ? ' has-expanded' : ''}`}
            >
              {ordered.map((service) => {
                return (
                  <ServiceCard
                    key={service.id}
                    service={service}
                    expanded={expandedSlug === service.slug}
                    onToggle={() => toggle(service.slug)}
                  />
                );
              })}
            </div>
          );
        })}
      </div>

      <section id="contact-cta" className="card services-cta">
        <div>
          <span className="eyebrow">Next step</span>
          <h2>Ready to eliminate busy work?</h2>
          <p>
            Tell us where time disappears — inbox thrash, support load, data entry, calendar chaos, pipeline follow-up,
            or brittle integrations. We will map a practical automation plan and a path to first wins.
          </p>
        </div>
        <div className="services-cta-actions">
          <a className="btn btn-primary btn-lg" href="/#contact">
            Schedule a free consultation
          </a>
          <Link className="btn btn-ghost btn-lg" to="/marketplace">
            Shop EspoCRM extensions
          </Link>
        </div>
      </section>
    </div>
  );
}
